import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import {
  AUTH_ERROR_MESSAGES,
  ROLE_LABELS,
  isAuthErrorCode,
  isRoleCode,
  passwordForgotFormSchema,
  passwordLoginFormSchema,
} from '@thrivehrm/shared';
import * as React from 'react';
import { z } from 'zod';
import { PersonAvatar } from '@/components/ui/avatar';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { usePasswordLogin, useAuthConfig } from '@/features/auth/api';
import { AuthHeading, AuthLayout } from '@/features/auth/auth-layout';
import { GoogleMark } from '@/features/auth/google-mark';
import { PasswordInput } from '@/features/auth/password-input';
import { Recaptcha, type RecaptchaHandle } from '@/features/auth/recaptcha';
import { ApiError } from '@/lib/api';
import { browser, devLogin, loadDevAccounts, safeNext } from '@/lib/session';

const searchSchema = z.object({
  /** Denial code from the Google callback (FR-AUTH-3/4, FR-AUTH-9). */
  error: z.string().optional().catch(undefined),
  /** Why the user is here: the session ended, or they just signed out. */
  reason: z.enum(['expired', 'signed_out']).optional().catch(undefined),
  /** Same-origin path to open after sign-in. */
  next: z.string().optional().catch(undefined),
  /** Prefill for the email field, e.g. after setting a password (FR-AUTH-17). */
  email: z.string().max(254).optional().catch(undefined),
  /** Set by the set / reset password pages: show "Password saved". */
  passwordSet: z
    .union([z.literal(1), z.literal('1')])
    .optional()
    .catch(undefined),
});

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  beforeLoad: ({ context, search }) => {
    // Already signed in: go where they were heading.
    if (context.auth.user) throw redirect({ href: safeNext(search.next) });
  },
  component: LoginPage,
});

/**
 * Sign-in page (DESIGN_SYSTEM §6 Login, US-01, US-12…US-14, FR-AUTH-1…12, ADR-091 (3); layout
 * T-101, password sign-in T-13A): one screen for everyone — "Continue with Google" (when the
 * server has it configured), then "or sign in with email" with email, password, Sign in and
 * "Forgot password?". The reCAPTCHA checkbox appears only after the API asks for it. Nothing on
 * the page depends on which method an email uses (FR-AUTH-19).
 */
function LoginPage() {
  const { error, reason, next, email, passwordSet } = Route.useSearch();
  const target = safeNext(next);
  const config = useAuthConfig();
  const [formError, setFormError] = React.useState<string | null>(null);
  const urlMessage = isAuthErrorCode(error)
    ? AUTH_ERROR_MESSAGES[error]
    : error
      ? AUTH_ERROR_MESSAGES.sign_in_failed
      : null;
  // A refusal from the form replaces whatever the address said.
  const notice = formError === null;
  const googleEnabled = config.data?.googleEnabled ?? config.isError;

  return (
    <AuthLayout titleId="login-title">
      <AuthHeading id="login-title" title="Sign in to ThriveHRM">
        {googleEnabled
          ? 'Use Continue with Google for your company Google account, or sign in with your email and password.'
          : 'Sign in with your email and password.'}
      </AuthHeading>
      {notice && urlMessage ? (
        <Banner tone="danger" title="Couldn't sign you in" live>
          {urlMessage}
        </Banner>
      ) : null}
      {notice && !urlMessage && passwordSet ? (
        <Banner tone="success" title="Password saved" live>
          Sign in with your new password.
        </Banner>
      ) : null}
      {notice && !urlMessage && !passwordSet && reason === 'expired' ? (
        <Banner tone="info" title="Your session ended" live>
          Your session expired. Sign in again to continue.
        </Banner>
      ) : null}
      {notice && !urlMessage && !passwordSet && reason === 'signed_out' ? (
        <Banner tone="success" live>
          You have signed out.
        </Banner>
      ) : null}
      {config.isPending ? (
        <Skeleton data-testid="google-skeleton" className="h-control-lg w-full rounded-md" />
      ) : googleEnabled ? (
        <>
          <GoogleButton target={target} />
          <div className="flex items-center gap-3 text-small text-muted-foreground">
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
            or sign in with email
            <span aria-hidden="true" className="h-px flex-1 bg-border" />
          </div>
        </>
      ) : null}
      <PasswordSignInForm
        target={target}
        initialEmail={email ?? ''}
        focusPassword={Boolean(passwordSet && email)}
        siteKey={config.data?.recaptchaSiteKey ?? null}
        googleEnabled={googleEnabled}
        error={formError}
        onError={setFormError}
      />
      <p className="text-small text-muted-foreground">
        Use the email HR registered for you: your V2 email, or your alternate email if you have no
        V2 email.
      </p>
      <DevLogin next={target} />
    </AuthLayout>
  );
}

function GoogleButton({ target }: { target: string }) {
  const href = `/api/v1/auth/google${target === '/' ? '' : `?next=${encodeURIComponent(target)}`}`;
  return (
    <Button asChild size="lg" className="w-full">
      <a href={href}>
        {/* Google's mark stays on white (brand guidelines); --ink-on-brand is white in both themes. */}
        <span className="flex size-7 items-center justify-center rounded-sm bg-on-brand">
          <GoogleMark />
        </span>
        Continue with Google
      </a>
    </Button>
  );
}

type FieldErrors = Partial<Record<'email' | 'password', string>>;

/** Messages for refusals that are not sign-in codes (rate limit, network, server). */
function errorText(error: string): string {
  return isAuthErrorCode(error) ? AUTH_ERROR_MESSAGES[error] : error;
}

/**
 * Email + password sign-in (FR-AUTH-10…12). Errors map to the shared messages; `captcha_required`
 * and `captcha_failed` show the reCAPTCHA checkbox above Sign in (each token is used once, so it
 * is cleared after every answer); `use_google` offers the Google button in the error.
 */
function PasswordSignInForm({
  target,
  initialEmail,
  focusPassword,
  siteKey,
  googleEnabled,
  error,
  onError,
}: {
  target: string;
  initialEmail: string;
  focusPassword: boolean;
  siteKey: string | null;
  googleEnabled: boolean;
  error: string | null;
  onError: (error: string | null) => void;
}) {
  const login = usePasswordLogin();
  const [email, setEmail] = React.useState(initialEmail);
  const [password, setPassword] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<FieldErrors>({});
  const [captchaShown, setCaptchaShown] = React.useState(false);
  const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const passwordRef = React.useRef<HTMLInputElement>(null);
  const recaptcha = React.useRef<RecaptchaHandle>(null);

  React.useEffect(() => {
    if (focusPassword) passwordRef.current?.focus();
  }, [focusPassword]);

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (login.isPending) return;
    const parsed = passwordLoginFormSchema.safeParse({ email, password });
    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if ((key === 'email' || key === 'password') && !errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      (errors.email ? emailRef : passwordRef).current?.focus();
      return;
    }
    setFieldErrors({});
    if (captchaShown && siteKey && !captchaToken) {
      onError('captcha_required');
      return;
    }
    onError(null);
    login.mutate(
      { ...parsed.data, ...(captchaToken ? { captchaToken } : {}) },
      {
        onSuccess: () => {
          browser.go(target);
        },
        onError: (err) => {
          if (captchaShown) recaptcha.current?.reset();
          const code = err instanceof ApiError ? err.problem.code : undefined;
          if (isAuthErrorCode(code)) {
            if (code === 'captcha_required' || code === 'captcha_failed') setCaptchaShown(true);
            if (code === 'invalid_credentials') {
              setPassword('');
              passwordRef.current?.focus();
            }
            onError(code);
            return;
          }
          onError(err instanceof ApiError ? err.message : AUTH_ERROR_MESSAGES.sign_in_failed);
        },
      },
    );
  };

  const forgotEmail = passwordForgotFormSchema.safeParse({ email });

  return (
    <form
      noValidate
      aria-label="Sign in with email"
      className="flex flex-col gap-4"
      onSubmit={submit}
    >
      {error ? (
        <Banner
          tone="danger"
          title="Couldn't sign you in"
          live
          action={
            error === 'use_google' && googleEnabled ? <GoogleButton target={target} /> : undefined
          }
        >
          {errorText(error)}
        </Banner>
      ) : null}
      <FormField label="Email" error={fieldErrors.email}>
        <Input
          ref={emailRef}
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          spellCheck={false}
          autoCapitalize="off"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
        />
      </FormField>
      <FormField label="Password" error={fieldErrors.password}>
        <PasswordInput
          ref={passwordRef}
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        />
      </FormField>
      {captchaShown && siteKey ? (
        <Recaptcha ref={recaptcha} siteKey={siteKey} onToken={setCaptchaToken} />
      ) : null}
      <Button
        type="submit"
        size="lg"
        variant={googleEnabled ? 'secondary' : 'primary'}
        className="w-full"
        loading={login.isPending}
      >
        Sign in
      </Button>
      <Link
        to="/forgot-password"
        search={forgotEmail.success ? { email: forgotEmail.data.email } : {}}
        className="self-start rounded-sm text-body text-primary underline-offset-4 hover:underline"
      >
        Forgot password?
      </Link>
    </form>
  );
}

/** FR-AUTH-8: shown only when the API answers `/auth/dev/accounts` (dev, flag on). */
function DevLogin({ next }: { next: string }) {
  const accounts = useQuery({
    queryKey: ['auth', 'dev-accounts'],
    queryFn: loadDevAccounts,
    retry: false,
  });
  const login = useMutation({ mutationFn: (userId: string) => devLogin(userId, next) });
  if (!accounts.data) return null;
  return (
    <div className="flex flex-col gap-3 border-t pt-5">
      <div>
        <h2 className="text-section text-foreground">Dev login as…</h2>
        <p className="text-small text-muted-foreground">Development only — no Google needed.</p>
      </div>
      {login.error ? (
        <Banner tone="danger" live>
          {login.error instanceof ApiError ? login.error.message : 'Sign-in failed.'}
        </Banner>
      ) : null}
      <ul
        className="flex max-h-80 flex-col divide-y overflow-y-auto rounded-lg border bg-surface"
        aria-label="Dev accounts"
      >
        {accounts.data.map((a) => (
          <li key={a.userId}>
            {/* min-h, not h-auto: tailwind-merge does not know `h-control-*`, so an h-auto
                override would lose to the Button's fixed 40 px height. */}
            <Button
              variant="ghost"
              className="min-h-16 w-full justify-start gap-3 rounded-none px-4 py-2.5 text-left"
              loading={login.isPending && login.variables === a.userId}
              disabled={login.isPending}
              onClick={() => {
                login.mutate(a.userId);
              }}
            >
              <PersonAvatar name={a.name} size="sm" />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-body-strong text-foreground">
                  {a.name}{' '}
                  <span className="text-small font-normal text-muted-foreground">#{a.empCode}</span>
                </span>
                <span className="truncate text-small font-normal text-muted-foreground">
                  {a.email} ·{' '}
                  {a.roles
                    .filter(isRoleCode)
                    .map((r) => ROLE_LABELS[r])
                    .join(', ')}
                </span>
              </span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
